import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Article } from 'src/app/services/article.service';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { ReadTrackingService } from 'src/app/services/read-tracking.service';

type NewsBisCardSize = 'medium' | 'small' | 'large';
const NEWS_BIS_BODY_VERTICAL_GAP_PX = 6;
const NEWS_BIS_LARGE_BODY_MAX_LINES = 5;
const NEWS_BIS_BODY_BLOCK_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'DIV',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'MAIN',
  'P',
  'PRE',
  'SECTION',
]);

@Component({
  selector: 'app-latest-articles-card',
  templateUrl: './latest-articles-card.component.html',
  styleUrls: ['./latest-articles-card.component.scss']
})
export class LatestArticlesCardComponent implements AfterViewInit, AfterViewChecked, OnChanges, OnDestroy {
  @ViewChildren('newsBisCard') newsBisCards: QueryList<ElementRef<HTMLElement>>;

  @Input() articles: Article[] | null = [];
  @Input() startIndex: number = 0;
  @Input() endIndex: number = 3;
  @Input() onArticleClick: (article: Article) => void = () => {};
  @Input() isLoading: boolean = false;
  @Input() layout: 'standard' | 'news-bis' = 'standard';
  @Input() cardFormats: NewsBisCardSize[] = [];

  environment = environment;
  assetUrl = environment.assetUrl;
  private bodyPreviewCache = new WeakMap<Article, string>();
  private newsBisCardsSub?: Subscription;
  private pendingNewsBisLayoutFrame?: number;
  private newsBisResizeObserver?: ResizeObserver;
  private textMeasureCanvas?: HTMLCanvasElement;

  constructor(
    private router: Router,
    private readTrackingService: ReadTrackingService
  ) {}

  ngAfterViewInit() {
    this.newsBisCardsSub = this.newsBisCards.changes.subscribe(() => {
      this.observeNewsBisCards();
      this.scheduleNewsBisTextLayout();
    });

    this.observeNewsBisCards();
    this.scheduleNewsBisTextLayout();

    const fonts = typeof document !== 'undefined' ? (document as any).fonts : null;
    if (fonts?.ready) {
      fonts.ready.then(() => this.scheduleNewsBisTextLayout());
    }
  }

  ngAfterViewChecked() {
    this.scheduleNewsBisTextLayout();
  }

  ngOnChanges() {
    this.scheduleNewsBisTextLayout();
  }

  ngOnDestroy() {
    this.newsBisCardsSub?.unsubscribe();
    this.newsBisResizeObserver?.disconnect();

    if (typeof window !== 'undefined' && this.pendingNewsBisLayoutFrame) {
      window.cancelAnimationFrame(this.pendingNewsBisLayoutFrame);
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.scheduleNewsBisTextLayout();
  }

  getThumbnailUrl(article: Article) {
    return `${this.assetUrl}/thumbnails/${article.thumbnail}`;
  }

  getBodyPreview(article: Article): string {
    const cached = this.bodyPreviewCache.get(article);

    if (cached !== undefined) {
      return cached;
    }

    const preview = this.toPlainText(article.body);
    this.bodyPreviewCache.set(article, preview);

    return preview;
  }

  navigateToArticle(article: Article) {
    article.is_read = true;
    this.router.navigate([`article/${article.uuid}`], {
      queryParams: this.readTrackingService.withHomepageSource({
        sort: 'publication_date',
        direction: 'desc',
      }),
    });
  }

  get displayedArticles(): Article[] {
    return (this.articles ?? []).slice(this.startIndex, this.endIndex);
  }

  getNewsBisCardSize(index: number): NewsBisCardSize {
    const explicitCardSize = this.cardFormats[index];

    if (explicitCardSize) {
      return explicitCardSize;
    }

    return this.getDefaultNewsBisCardSize(index);
  }

  private getDefaultNewsBisCardSize(index: number): NewsBisCardSize {
    const pattern: Array<'medium' | 'small' | 'large'> = ['medium', 'small', 'large'];
    return pattern[(this.startIndex + index) % pattern.length];
  }

  getNewsBisCardClass(index: number): string {
    return `news-bis-card--${this.getNewsBisCardSize(index)}`;
  }

  getNewsBisCardLayoutClass(index: number): string {
    switch (this.getNewsBisCardSize(index)) {
      case 'medium':
        return 'aspect-[6/3] flex-row';
      case 'small':
        return 'aspect-[6/3] flex-col';
      case 'large':
        return 'aspect-[595/700] flex-col';
    }
  }

  getNewsBisContentClass(index: number): string {
    switch (this.getNewsBisCardSize(index)) {
      case 'medium':
        return 'w-1/2 basis-1/2 flex-none justify-start';
      case 'small':
        return 'h-full flex-1';
      case 'large':
        return 'h-1/2 flex-none';
    }
  }

  getNewsBisTitleClass(index: number): string {
    switch (this.getNewsBisCardSize(index)) {
      case 'medium':
        return 'mx-0 my-3 text-base';
      case 'small':
        return 'mx-0 my-0 text-[1.05rem]';
      case 'large':
        return 'mx-0 my-0 text-[1.12rem]';
    }
  }

  getNewsBisImageClass(index: number): string {
    switch (this.getNewsBisCardSize(index)) {
      case 'medium':
        return 'h-full basis-1/2 flex-none bg-cover';
      case 'large':
        return 'h-1/2 w-full flex-none bg-cover';
      case 'small':
        return '';
    }
  }

  private scheduleNewsBisTextLayout() {
    if (this.layout !== 'news-bis' || this.isLoading || typeof window === 'undefined') {
      return;
    }

    if (this.pendingNewsBisLayoutFrame) {
      return;
    }

    this.pendingNewsBisLayoutFrame = window.requestAnimationFrame(() => {
      this.pendingNewsBisLayoutFrame = undefined;
      this.updateNewsBisTextClamps();
    });
  }

  private updateNewsBisTextClamps() {
    if (!this.newsBisCards?.length) {
      return;
    }

    this.newsBisCards.forEach((cardRef, index) => {
      const card = cardRef.nativeElement;
      const content = card.querySelector<HTMLElement>('.news-bis-card__content');
      const date = card.querySelector<HTMLElement>('.news-bis-card__date');
      const title = card.querySelector<HTMLElement>('.news-bis-card__title');
      const body = card.querySelector<HTMLElement>('.news-bis-card__body');
      const article = this.displayedArticles[index];
      const titleText = article?.title || (title ? this.getFullText(title) : '');
      const bodyText = article ? this.getBodyPreview(article) : (body ? this.getFullText(body) : '');

      if (!content || !title) {
        return;
      }

      const contentHeight = this.getInnerHeight(content);
      const dateHeight = this.getOuterHeight(date);
      const titleLineHeight = this.getLineHeight(title);

      if (card.classList.contains('news-bis-card--medium')) {
        const titleLines = Math.min(5, Math.max(1, Math.floor((contentHeight - dateHeight - 2) / titleLineHeight)));
        this.renderWrappedText(title, titleText, titleLines);
        return;
      }

      this.renderWrappedText(title, titleText, 3);

      if (!body) {
        return;
      }

      body.style.marginTop = '0px';
      body.style.marginBottom = '0px';

      const titleHeight = this.getOuterHeight(title);
      const bodyLineHeight = this.getLineHeight(body);
      const bodyGap = this.getNewsBisBodyVerticalGap(contentHeight, titleHeight, dateHeight, bodyLineHeight);
      const bodySpace = contentHeight - dateHeight - titleHeight - (bodyGap * 2);
      let bodyLines = Math.max(0, Math.floor((bodySpace - 2) / bodyLineHeight));
      bodyLines = this.limitNewsBisBodyLines(card, bodyLines);

      this.renderWrappedText(body, bodyText, bodyLines);
      this.setNewsBisBodyVerticalGap(body, bodyGap);

      while (bodyLines > 0 && this.getStackedContentHeight(date, title, body) > contentHeight) {
        bodyLines--;
        this.renderWrappedText(body, bodyText, bodyLines);
        this.setNewsBisBodyVerticalGap(body, bodyGap);
      }
    });
  }

  private getInnerHeight(element: HTMLElement): number {
    const styles = window.getComputedStyle(element);
    return element.clientHeight
      - this.toNumber(styles.paddingTop)
      - this.toNumber(styles.paddingBottom);
  }

  private getOuterHeight(element?: HTMLElement | null): number {
    if (!element) {
      return 0;
    }

    const styles = window.getComputedStyle(element);
    return element.getBoundingClientRect().height
      + this.toNumber(styles.marginTop)
      + this.toNumber(styles.marginBottom);
  }

  private getLineHeight(element: HTMLElement): number {
    const styles = window.getComputedStyle(element);
    const lineHeight = this.toNumber(styles.lineHeight);

    if (lineHeight > 0) {
      return lineHeight;
    }

    return this.toNumber(styles.fontSize) * 1.2;
  }

  private toNumber(value: string): number {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private getVerticalMargins(element: HTMLElement): { top: number; bottom: number } {
    const styles = window.getComputedStyle(element);
    return {
      top: this.toNumber(styles.marginTop),
      bottom: this.toNumber(styles.marginBottom),
    };
  }

  private getStackedContentHeight(...elements: Array<HTMLElement | null>): number {
    return elements.reduce((height, element) => height + this.getOuterHeight(element), 0);
  }

  private getNewsBisBodyVerticalGap(
    contentHeight: number,
    titleHeight: number,
    dateHeight: number,
    bodyLineHeight: number
  ): number {
    const maxGapWithOneBodyLine = Math.max(0, (contentHeight - titleHeight - dateHeight - bodyLineHeight) / 2);

    return Math.min(NEWS_BIS_BODY_VERTICAL_GAP_PX, maxGapWithOneBodyLine);
  }

  private limitNewsBisBodyLines(card: HTMLElement, availableLines: number): number {
    return card.classList.contains('news-bis-card--large')
      ? Math.min(NEWS_BIS_LARGE_BODY_MAX_LINES, availableLines)
      : availableLines;
  }

  private setNewsBisBodyVerticalGap(body: HTMLElement, gap: number) {
    if (body.style.display === 'none') {
      body.style.marginTop = '0px';
      body.style.marginBottom = '0px';
      return;
    }

    body.style.marginTop = `${gap}px`;
    body.style.marginBottom = `${gap}px`;
  }

  private renderWrappedText(element: HTMLElement, text: string, maxLines: number) {
    const normalizedText = this.normalizeStructuredText(text);
    const safeMaxLines = Math.max(0, Math.floor(maxLines));

    element.style.display = safeMaxLines <= 0 ? 'none' : '';
    element.style.height = safeMaxLines <= 0 ? '0px' : '';

    if (!normalizedText || safeMaxLines <= 0) {
      element.textContent = '';
      return;
    }

    const lineHeight = this.getLineHeight(element);
    const availableWidth = Math.floor(element.getBoundingClientRect().width);

    if (availableWidth <= 0) {
      element.textContent = '';
      return;
    }

    const lines = this.wrapTextToLines(normalizedText, element, availableWidth, safeMaxLines);
    element.textContent = lines.join('\n');
    element.style.height = `${lines.length * lineHeight}px`;
  }

  private wrapTextToLines(text: string, element: HTMLElement, width: number, maxLines: number): string[] {
    const context = this.getTextMeasureContext(element);
    const hardLines = text.split('\n');

    if (!context) {
      const fallbackLines = hardLines.slice(0, maxLines);

      if (hardLines.length > maxLines && fallbackLines.length > 0) {
        const lastLineIndex = fallbackLines.length - 1;
        fallbackLines[lastLineIndex] = this.ellipsizeText(fallbackLines[lastLineIndex], width, null, true);
      }

      return fallbackLines;
    }

    const lines: string[] = [];
    for (let hardLineIndex = 0; hardLineIndex < hardLines.length && lines.length < maxLines; hardLineIndex++) {
      let remaining = hardLines[hardLineIndex];
      const hasFollowingText = hardLines
        .slice(hardLineIndex + 1)
        .some((line) => line.length > 0);

      if (!remaining) {
        const isLastAvailableLine = lines.length === maxLines - 1;
        lines.push(isLastAvailableLine && hasFollowingText ? '…' : '');
        continue;
      }

      while (remaining && lines.length < maxLines) {
        const isLastAvailableLine = lines.length === maxLines - 1;

        if (isLastAvailableLine) {
          lines.push(this.ellipsizeText(remaining, width, context, hasFollowingText));
          break;
        }

        const line = this.takeFittingLine(remaining, width, context);
        lines.push(line);
        remaining = this.trimLeadingWhitespace(remaining.slice(line.length));
      }
    }

    return lines;
  }

  private takeFittingLine(text: string, width: number, context: CanvasRenderingContext2D): string {
    if (this.measureTextWidth(text, context) <= width) {
      return text;
    }

    let start = 0;
    let end = text.length;

    while (start < end) {
      const middle = Math.floor((start + end + 1) / 2);
      const candidate = text.slice(0, middle);

      if (this.measureTextWidth(candidate, context) <= width) {
        start = middle;
      } else {
        end = middle - 1;
      }
    }

    const fitted = text.slice(0, Math.max(1, start));
    const lastSpace = fitted.lastIndexOf(' ');

    if (lastSpace > 0) {
      return fitted.slice(0, lastSpace);
    }

    return fitted;
  }

  private ellipsizeText(
    text: string,
    width: number,
    context: CanvasRenderingContext2D | null,
    forceEllipsis: boolean = false
  ): string {
    const ellipsis = '…';
    const trimmedText = this.trimTrailingWhitespace(text);

    if (!context) {
      return forceEllipsis ? `${trimmedText}${ellipsis}` : text;
    }

    if (!forceEllipsis && this.measureTextWidth(text, context) <= width) {
      return text;
    }

    if (this.measureTextWidth(ellipsis, context) > width) {
      return '';
    }

    let start = 0;
    let end = trimmedText.length;

    while (start < end) {
      const middle = Math.floor((start + end + 1) / 2);
      const candidate = `${this.trimTrailingWhitespace(trimmedText.slice(0, middle))}${ellipsis}`;

      if (this.measureTextWidth(candidate, context) <= width) {
        start = middle;
      } else {
        end = middle - 1;
      }
    }

    return `${this.trimTrailingWhitespace(trimmedText.slice(0, start))}${ellipsis}`;
  }

  private getTextMeasureContext(element: HTMLElement): CanvasRenderingContext2D | null {
    if (typeof document === 'undefined') {
      return null;
    }

    if (!this.textMeasureCanvas) {
      this.textMeasureCanvas = document.createElement('canvas');
    }

    const context = this.textMeasureCanvas.getContext('2d');

    if (!context) {
      return null;
    }

    const styles = window.getComputedStyle(element);
    context.font = [
      styles.fontStyle,
      styles.fontVariant,
      styles.fontWeight,
      styles.fontSize,
      styles.fontFamily,
    ].join(' ');

    return context;
  }

  private measureTextWidth(text: string, context: CanvasRenderingContext2D): number {
    return context.measureText(text).width;
  }

  private getFullText(element: HTMLElement): string {
    return element.getAttribute('data-full-text') || '';
  }

  private trimTrailingWhitespace(value: string): string {
    return value.replace(/\s+$/g, '');
  }

  private trimLeadingWhitespace(value: string): string {
    return value.replace(/^\s+/g, '');
  }

  private observeNewsBisCards() {
    this.newsBisResizeObserver?.disconnect();

    if (typeof ResizeObserver === 'undefined' || this.layout !== 'news-bis') {
      return;
    }

    this.newsBisResizeObserver = new ResizeObserver(() => {
      this.scheduleNewsBisTextLayout();
    });

    this.newsBisCards?.forEach((cardRef) => {
      this.newsBisResizeObserver.observe(cardRef.nativeElement);
    });
  }

  private toPlainText(html: string = ''): string {
    if (!html) {
      return '';
    }

    if (typeof DOMParser === 'undefined') {
      const fallbackText = html
        .replace(/<br\s*\/?\s*>/gi, '\n')
        .replace(/<li(?:\s[^>]*)?>/gi, '• ')
        .replace(/<\/li>/gi, '\n')
        .replace(/<\/(?:p|div|h[1-6]|blockquote|pre|tr)>/gi, '\n')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ');

      return this.normalizeStructuredText(fallbackText);
    }

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const structuredText = Array.from(parsed.body.childNodes)
      .map((node) => this.toStructuredText(node))
      .join('');

    return this.normalizeStructuredText(structuredText);
  }

  private toStructuredText(node: Node): string {
    if (node.nodeType === 3) {
      return node.textContent || '';
    }

    if (node.nodeType !== 1) {
      return '';
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toUpperCase();

    if (['SCRIPT', 'STYLE', 'IFRAME', 'IMG', 'VIDEO'].includes(tagName)) {
      return '';
    }

    if (tagName === 'BR') {
      return '\n';
    }

    const content = Array.from(element.childNodes)
      .map((child) => this.toStructuredText(child))
      .join('');

    if (tagName === 'LI') {
      return `${this.getListItemPrefix(element)}${content.trim()}\n`;
    }

    if (tagName === 'TD' || tagName === 'TH') {
      return `${content} `;
    }

    if (tagName === 'TR') {
      return `${this.trimTrailingWhitespace(content)}\n`;
    }

    return NEWS_BIS_BODY_BLOCK_TAGS.has(tagName) ? `${content}\n` : content;
  }

  private getListItemPrefix(listItem: HTMLElement): string {
    const list = listItem.parentElement;

    if (
      listItem.getAttribute('data-list') === 'bullet'
      || !list
      || list.tagName.toUpperCase() !== 'OL'
    ) {
      return '• ';
    }

    const explicitValue = Number(listItem.getAttribute('value'));
    if (Number.isFinite(explicitValue) && explicitValue > 0) {
      return `${explicitValue}. `;
    }

    const start = Number(list.getAttribute('start'));
    const firstIndex = Number.isFinite(start) && start > 0 ? start : 1;
    const itemIndex = Array.from(list.children)
      .filter((child) => child.tagName.toUpperCase() === 'LI')
      .indexOf(listItem);

    return `${firstIndex + Math.max(0, itemIndex)}. `;
  }

  private normalizeStructuredText(text: string): string {
    return text
      .replace(/\u00a0/g, ' ')
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map((line) => line.replace(/[\t\f\v ]+/g, ' ').trim())
      .join('\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }
}
